import React, { useState, useEffect, useCallback } from "react";
import {
  Truck, Plus, CheckCircle, Clock, ChevronDown, ChevronUp,
  X, RefreshCw, FileText, ShoppingCart, Package, AlertCircle, Link2, Trash2, Edit
} from "lucide-react";
import { apiFetch } from "../utils/api";

/**
 * Delivery Notes tab of the Purchasing hub — goods-received workflow
 * over /api/inventory/delivery-notes.
 *
 * Each note records lines delivered against a purchase order. Editing
 * qty_delivered is only a draft until "confirm": confirmation is the
 * point where the backend actually increments stock levels and updates
 * the parent PO's received counts (see routes/delivery_notes.py —
 * confirm/unconfirm are POSTs, not PATCHes, because they mutate
 * inventory). Unconfirming reverses those effects.
 */

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  var map = {
    draft:     "bg-gray-100 text-gray-600",
    confirmed: "bg-green-100 text-green-700",
  };
  return (
    <span className={"text-xs font-medium px-2 py-0.5 rounded-full " + (map[status] || "bg-gray-100 text-gray-500")}>
      {status}
    </span>
  );
}

// ── DN Detail (expanded view + confirm flow) ──────────────────────────────────
function DNDetail({ dn: initialDn, onUpdated, onClose, onDelete }) {
  var [dn, setDn] = useState(initialDn);
  var [lines, setLines] = useState(function() {
    return (initialDn.lines || []).map(function(l) {
      return Object.assign({}, l);
    });
  });
  var [saving, setSaving] = useState(false);
  var [confirming, setConfirming] = useState(false);
  var [unconfirming, setUnconfirming] = useState(false);
  var [msg, setMsg] = useState(null);
  var [notes, setNotes] = useState(initialDn.notes || "");

  var isConfirmed = dn.status === "confirmed";

  var updateQty = function(idx, val) {
    setLines(function(prev) {
      var arr = prev.slice();
      arr[idx] = Object.assign({}, arr[idx], { qty_delivered: val === "" ? "" : val });
      return arr;
    });
  };

  var handleSaveLines = async function() {
    setSaving(true);
    try {
      var parsedLines = lines.map(function(l) {
        return Object.assign({}, l, { qty_delivered: parseFloat(l.qty_delivered) || 0 });
      });
      var res = await apiFetch("/api/inventory/delivery-notes/" + dn.id, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lines: parsedLines })
      });
      if (res.ok) {
        var updated = await res.json();
        setDn(updated);
        setLines(updated.lines || []);
        setMsg("Saved.");
        setTimeout(function() { setMsg(null); }, 2000);
        onUpdated(updated);
      }
    } finally {
      setSaving(false);
    }
  };

  var handleSaveNotes = async function() {
    setSaving(true);
    try {
      var res = await apiFetch("/api/inventory/delivery-notes/" + dn.id, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: notes })
      });
      if (res.ok) {
        var updated = await res.json();
        setDn(updated);
        setMsg("Notes saved.");
        setTimeout(function() { setMsg(null); }, 2000);
        onUpdated(updated);
      }
    } finally { setSaving(false); }
  };

  var handleUnconfirm = async function() {
    if (!window.confirm("Revert " + dn.dn_number + " to draft? Stock levels and PO received quantities will be reversed.")) return;
    setUnconfirming(true);
    try {
      var res = await apiFetch("/api/inventory/delivery-notes/" + dn.id + "/unconfirm", {
        method: "POST"
      });
      if (res.ok) {
        var updated = await res.json();
        setDn(updated);
        setLines(updated.lines || []);
        setNotes(updated.notes || "");
        setMsg("Reverted to draft — stock reversed.");
        onUpdated(updated);
      } else {
        var err = await res.json().catch(function() { return {}; });
        setMsg(err.detail || "Failed to revert.");
      }
    } finally { setUnconfirming(false); }
  };

  var handleConfirm = async function() {
    if (!window.confirm("Confirm receipt of " + dn.dn_number + "? This will update stock levels and mark PO lines as received.")) return;
    setConfirming(true);
    try {
      // Always save current line qty_delivered values first, parsed as floats
      var parsedLines = lines.map(function(l) {
        return Object.assign({}, l, { qty_delivered: parseFloat(l.qty_delivered) || 0 });
      });
      var saveRes = await apiFetch("/api/inventory/delivery-notes/" + dn.id, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lines: parsedLines })
      });
      if (!saveRes.ok) { setMsg("Failed to save quantities before confirming."); return; }
      var confirmRes = await apiFetch("/api/inventory/delivery-notes/" + dn.id + "/confirm", {
        method: "POST"
      });
      if (confirmRes.ok) {
        var updated = await confirmRes.json();
        setDn(updated);
        setLines(updated.lines || []);
        setMsg("Delivery confirmed — stock updated.");
        onUpdated(updated);
      }
    } finally { setConfirming(false); }
  };

  var inp = "w-full border border-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-purple-400 focus:outline-none";

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center">
            <Truck className="h-4.5 w-4.5 text-teal-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">{dn.dn_number}</p>
            <p className="text-xs text-gray-400">{dn.supplier_name} · PO: {dn.purchase_order_number}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={dn.status} />
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Lines table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-400 font-medium border-b border-gray-100">
            <tr>
              <th className="px-4 py-2 text-left">Item</th>
              <th className="px-4 py-2 text-left">Supplier Code</th>
              <th className="px-4 py-2 text-right">Qty Expected</th>
              <th className="px-4 py-2 text-right">Qty Delivered</th>
              <th className="px-4 py-2 text-right">Unit Price</th>
              <th className="px-4 py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {lines.map(function(line, i) {
              return (
                <tr key={i} className={"hover:bg-gray-50 " + (line.confirmed ? "bg-green-50/30" : "")}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-800">{line.inventory_item_description}</div>
                    {line.inventory_item_size && <div className="text-gray-400">{line.inventory_item_size}</div>}
                  </td>
                  <td className="px-4 py-2 font-mono text-gray-500">{line.supplier_code || "—"}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{line.qty_ordered}</td>
                  <td className="px-4 py-2 text-right w-24">
                    {isConfirmed ? (
                      <span className="font-semibold text-green-700">{line.qty_delivered}</span>
                    ) : (
                      <input
                        type="number" min="0" step="any"
                        value={line.qty_delivered}
                        onChange={function(e) { updateQty(i, e.target.value); }}
                        className={inp + " text-right w-20"}
                      />
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-500">{line.unit_price ? "£" + line.unit_price : "—"}</td>
                  <td className="px-4 py-2 text-center">
                    {line.confirmed
                      ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Received</span>
                      : <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Pending</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Notes row */}
      <div className="px-5 py-3 border-t border-gray-100 flex items-start gap-3">
        <span className="text-xs text-gray-400 pt-1 w-12 shrink-0">Notes</span>
        {isConfirmed ? (
          <span className="text-xs text-gray-600">{dn.notes || <span className="text-gray-300">—</span>}</span>
        ) : (
          <div className="flex-1 flex gap-2">
            <textarea
              value={notes}
              onChange={function(e) { setNotes(e.target.value); }}
              rows={2}
              placeholder="Add notes…"
              className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-purple-400 focus:outline-none resize-none"
            />
            <button onClick={handleSaveNotes} disabled={saving}
              className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 text-gray-600 disabled:opacity-50 self-start">
              Save
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      {!isConfirmed && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <div>
            {msg && <span className="text-xs text-green-600">{msg}</span>}
          </div>
          <div className="flex items-center gap-2">
            {onDelete && (
              <button onClick={onDelete}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 rounded-lg font-medium">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )}
            <button onClick={handleSaveLines} disabled={saving}
              className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-white font-medium text-gray-600 disabled:opacity-50">
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button onClick={handleConfirm} disabled={confirming}
              className="flex items-center gap-1.5 text-xs px-4 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium disabled:opacity-50">
              {confirming ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
              {confirming ? "Confirming…" : "Confirm Receipt"}
            </button>
          </div>
        </div>
      )}
      {isConfirmed && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-amber-50/50 rounded-b-xl">
          <div className="flex items-center gap-2 text-xs text-amber-700">
            {dn.invoice_number && <><Link2 className="h-3.5 w-3.5" /> Linked invoice: <strong>{dn.invoice_number}</strong></>}
            {msg && <span className="text-green-600 ml-2">{msg}</span>}
          </div>
          <button onClick={handleUnconfirm} disabled={unconfirming}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-amber-300 text-amber-700 hover:bg-amber-100 rounded-lg font-medium disabled:opacity-50">
            {unconfirming ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Edit className="h-3.5 w-3.5" />}
            {unconfirming ? "Reverting…" : "Edit (Revert to Draft)"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main DeliveryNotes component ──────────────────────────────────────────────
const DeliveryNotes = ({ initialPoId, initialPoNumber }) => {
  var [dns, setDns] = useState([]);
  var [loading, setLoading] = useState(true);
  var [expanded, setExpanded] = useState(null);
  var [creating, setCreating] = useState(false);
  var [search, setSearch] = useState("");

  // PO search for create flow
  var [pos, setPos] = useState([]);
  var [poSearch, setPoSearch] = useState(initialPoNumber || "");
  var [poResults, setPoResults] = useState([]);
  var [selectedPo, setSelectedPo] = useState(null);
  var [createMsg, setCreateMsg] = useState(null);

  var [filterStatus, setFilterStatus] = useState("");

  var load = useCallback(function() {
    setLoading(true);
    apiFetch("/api/inventory/delivery-notes?limit=200")
      .then(function(r) { return r.ok ? r.json() : { items: [] }; })
      .then(function(d) { setDns(d.items || []); })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }, []);

  useEffect(function() { load(); }, [load]);

  // Load POs for search
  useEffect(function() {
    apiFetch("/api/inventory/purchase-orders?limit=500")
      .then(function(r) { return r.ok ? r.json() : { items: [] }; })
      .then(function(d) { setPos(d.items || []); });
  }, []);

  // Filter POs by search
  useEffect(function() {
    if (!poSearch) { setPoResults([]); return; }
    var q = poSearch.toLowerCase();
    var results = pos.filter(function(p) {
      return (
        (p.po_number || "").toLowerCase().includes(q) ||
        (p.supplier_name || "").toLowerCase().includes(q)
      );
    }).filter(function(p) {
      return !["received", "cancelled"].includes(p.status);
    }).slice(0, 8);
    setPoResults(results);
  }, [poSearch, pos]);

  var handleCreateFromPo = async function(po) {
    setCreating(true);
    setCreateMsg(null);
    try {
      var res = await apiFetch("/api/inventory/delivery-notes/from-po/" + po.id, {
        method: "POST"
      });
      if (res.ok) {
        var newDn = await res.json();
        setDns(function(prev) { return [newDn].concat(prev); });
        setExpanded(newDn.id);
        setSelectedPo(null);
        setPoSearch("");
        setPoResults([]);
      } else {
        var err = await res.json();
        setCreateMsg(err.detail || "Error creating delivery note");
      }
    } catch(e) {
      setCreateMsg("Network error");
    } finally { setCreating(false); }
  };

  var handleUpdated = function(updatedDn) {
    setDns(function(prev) {
      return prev.map(function(d) { return d.id === updatedDn.id ? updatedDn : d; });
    });
  };

  var handleDelete = async function(dn) {
    if (!window.confirm("Delete " + dn.dn_number + "? This cannot be undone.")) return;
    var res = await apiFetch("/api/inventory/delivery-notes/" + dn.id, { method: "DELETE" });
    if (res.ok || res.status === 204) {
      setDns(function(prev) { return prev.filter(function(d) { return d.id !== dn.id; }); });
      setExpanded(null);
    } else {
      var err = await res.json().catch(function() { return {}; });
      alert(err.detail || "Could not delete delivery note.");
    }
  };

  var filtered = dns.filter(function(d) {
    if (filterStatus && d.status !== filterStatus) return false;
    if (search) {
      var q = search.toLowerCase();
      return (d.dn_number || "").toLowerCase().includes(q)
        || (d.supplier_name || "").toLowerCase().includes(q)
        || (d.purchase_order_number || "").toLowerCase().includes(q);
    }
    return true;
  });

  var poStatusCls = function(s) {
    return { draft: "bg-gray-100 text-gray-600", submitted: "bg-amber-100 text-amber-700", approved: "bg-blue-100 text-blue-700", sent: "bg-purple-100 text-purple-700", partial: "bg-cyan-100 text-cyan-700", received: "bg-green-100 text-green-700", pending: "bg-orange-100 text-orange-700" }[s] || "bg-gray-100 text-gray-500";
  };

  return (
    <div className="space-y-5">

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={function() { setExpanded("__create__"); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg font-medium">
          <Plus className="h-4 w-4" /> Create Delivery Note
        </button>
        <div className="relative">
          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={function(e) { setSearch(e.target.value); }}
            placeholder="Search DN, supplier or PO…"
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:outline-none w-56"
          />
        </div>
        <select value={filterStatus} onChange={function(e) { setFilterStatus(e.target.value); }}
          className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-teal-500">
          <option value="">All</option>
          <option value="draft">Draft</option>
          <option value="confirmed">Confirmed</option>
        </select>
        <button onClick={load} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
          <RefreshCw className="h-4 w-4 text-gray-500" />
        </button>
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} delivery note{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Create panel */}
      {expanded === "__create__" && (
        <div className="bg-white rounded-xl border border-teal-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <Plus className="h-4 w-4 text-teal-600" /> Create Delivery Note from Purchase Order
            </p>
            <button onClick={function() { setExpanded(null); setSelectedPo(null); setPoSearch(""); setPoResults([]); }}
              className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by PO number or supplier…"
              value={poSearch}
              onChange={function(e) { setPoSearch(e.target.value); setSelectedPo(null); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 focus:outline-none"
            />
            {poResults.length > 0 && !selectedPo && (
              <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg divide-y divide-gray-50">
                {poResults.map(function(po) {
                  return (
                    <button key={po.id} onClick={function() { setSelectedPo(po); setPoSearch(po.po_number); setPoResults([]); }}
                      className="w-full text-left px-3 py-2 hover:bg-teal-50 text-sm flex items-center justify-between">
                      <div>
                        <span className="font-mono font-semibold text-gray-800">{po.po_number}</span>
                        <span className="ml-2 text-gray-500">{po.supplier_name}</span>
                        <span className="ml-2 text-gray-400 text-xs">{po.lines ? po.lines.length + " lines" : ""}</span>
                      </div>
                      <span className={"text-xs px-2 py-0.5 rounded-full font-medium " + poStatusCls(po.status)}>{po.status}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {selectedPo && (
            <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-lg px-4 py-3">
              <ShoppingCart className="h-4 w-4 text-teal-600 shrink-0" />
              <div className="flex-1 text-sm">
                <span className="font-semibold text-gray-800">{selectedPo.po_number}</span>
                <span className="ml-2 text-gray-500">{selectedPo.supplier_name}</span>
                <span className={"ml-2 text-xs px-2 py-0.5 rounded-full font-medium " + poStatusCls(selectedPo.status)}>{selectedPo.status}</span>
                <span className="ml-2 text-xs text-gray-400">{(selectedPo.lines || []).length} line{(selectedPo.lines || []).length !== 1 ? "s" : ""}</span>
              </div>
              <button
                onClick={function() { handleCreateFromPo(selectedPo); }}
                disabled={creating}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg font-medium disabled:opacity-50">
                {creating ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Truck className="h-3.5 w-3.5" />}
                {creating ? "Creating…" : "Create Delivery Note"}
              </button>
            </div>
          )}
          {createMsg && <p className="text-xs text-red-600">{createMsg}</p>}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
          <RefreshCw className="h-5 w-5 animate-spin" /> Loading delivery notes…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Truck className="h-10 w-10 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No delivery notes yet.</p>
          <p className="text-gray-400 text-xs mt-1">Create one from a Purchase Order when goods arrive.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(function(dn) {
            var isOpen = expanded === dn.id;
            var lineCount = (dn.lines || []).length;
            var confirmedCount = (dn.lines || []).filter(function(l) { return l.confirmed; }).length;
            return (
              <div key={dn.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Row header */}
                <button
                  onClick={function() { setExpanded(isOpen ? null : dn.id); }}
                  className="w-full flex items-center gap-4 px-5 py-3 hover:bg-gray-50 text-left">
                  <div className={"w-8 h-8 rounded-lg flex items-center justify-center shrink-0 " + (dn.status === "confirmed" ? "bg-green-100" : "bg-teal-100")}>
                    {dn.status === "confirmed"
                      ? <CheckCircle className="h-4 w-4 text-green-600" />
                      : <Truck className="h-4 w-4 text-teal-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800 font-mono">{dn.dn_number}</span>
                      <StatusBadge status={dn.status} />
                      {dn.invoice_number && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <FileText className="h-3 w-3" />{dn.invoice_number}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {dn.supplier_name} · PO: {dn.purchase_order_number} · {lineCount} line{lineCount !== 1 ? "s" : ""}
                      {dn.status === "draft" && lineCount > 0 && (
                        <span className="ml-2 text-amber-600">{confirmedCount}/{lineCount} confirmed</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-gray-400">{dn.created_at ? dn.created_at.slice(0, 10) : ""}</span>
                    {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-gray-100">
                    <DNDetail
                      dn={dn}
                      onUpdated={handleUpdated}
                      onClose={null}
                      onDelete={dn.status === "draft" ? function() { handleDelete(dn); } : null}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DeliveryNotes;
